import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { portalAPI, paymentsAPI } from '../../lib/api';
import { formatDate, formatCurrency, getStatusColor } from '../../lib/utils';
import { 
  Scale, Briefcase, FileText, MessageSquare, DollarSign, Calendar, 
  Upload, Send, LogOut, User, ChevronRight, AlertCircle, CheckCircle,
  Clock, Plus, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { PageLoader } from '../../components/common/LoadingSpinner';

export const PortalDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [messageDialog, setMessageDialog] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [messageForm, setMessageForm] = useState({ subject: '', content: '', case_id: '' });
  const [uploadForm, setUploadForm] = useState({ name: '', description: '', case_id: '', file: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Check for token in URL (from Google OAuth redirect)
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      localStorage.setItem('token', tokenFromUrl);
      // Clean the URL
      window.history.replaceState({}, document.title, '/portal');
      
      // Fetch user info and store it
      fetchUserInfo(tokenFromUrl);
    } else {
      // Check existing auth
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.role || (user.role !== 'client' && user.role !== 'owner' && user.role !== 'admin')) {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/portal/login');
          return;
        }
      }
      fetchDashboard();
    }
  }, [searchParams]);

  const fetchUserInfo = async (token) => {
    try {
      const response = await portalAPI.getDashboard();
      // Store user info from dashboard response
      if (response.data?.client) {
        localStorage.setItem('user', JSON.stringify({
          ...response.data.client,
          role: 'client'
        }));
      }
      setDashboardData(response.data);
      toast.success('Welcome to your Client Portal!');
    } catch (err) {
      console.error('Failed to load portal after Google login:', err);
      localStorage.removeItem('token');
      navigate('/portal/login?error=auth_failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await portalAPI.getDashboard();
      setDashboardData(response.data);
    } catch (err) {
      console.error('Failed to load portal:', err);
      if (err.response?.status === 403 || err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/portal/login');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await portalAPI.sendMessage(messageForm);
      toast.success('Message sent successfully');
      setMessageDialog(false);
      setMessageForm({ subject: '', content: '', case_id: '' });
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }
    
    setSubmitting(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        await portalAPI.uploadDocument({
          name: uploadForm.name || uploadForm.file.name,
          file_data: base64,
          mime_type: uploadForm.file.type,
          case_id: uploadForm.case_id || null,
          description: uploadForm.description
        });
        toast.success('Document uploaded successfully');
        setUploadDialog(false);
        setUploadForm({ name: '', description: '', case_id: '', file: null });
        fetchDashboard();
      };
      reader.readAsDataURL(uploadForm.file);
    } catch (err) {
      toast.error('Failed to upload document');
      setSubmitting(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      const response = await paymentsAPI.createCheckout(invoiceId, window.location.origin);
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      toast.error('Failed to initiate payment');
    }
  };

  if (loading) {
    return <PageLoader text="Loading your portal..." />;
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Unable to Load Portal</h2>
            <p className="text-slate-500 mb-6">Please try again or contact your legal team.</p>
            <Button onClick={() => navigate('/portal/login')}>Back to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { client, stats, cases, recent_documents, pending_invoices, recent_messages, upcoming_dates } = dashboardData;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <Scale className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">Client Portal</h1>
                <p className="text-xs text-slate-500">LegalFlow</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900">{client.name}</p>
                <p className="text-xs text-slate-500">{client.email}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome & Quick Stats */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome back, {client.name.split(' ')[0]}</h2>
          <p className="text-slate-500">Here's an overview of your legal matters</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="portal-stats">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.active_cases}</p>
                <p className="text-xs text-slate-500">Active Cases</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total_documents}</p>
                <p className="text-xs text-slate-500">Documents</p>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.pending_invoices > 0 ? 'border-orange-200' : ''}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${stats.pending_invoices > 0 ? 'bg-orange-100' : 'bg-green-100'}`}>
                <DollarSign className={`h-5 w-5 ${stats.pending_invoices > 0 ? 'text-orange-600' : 'text-green-600'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.total_owed)}</p>
                <p className="text-xs text-slate-500">{stats.pending_invoices} Invoice(s) Due</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.upcoming_dates}</p>
                <p className="text-xs text-slate-500">Upcoming Dates</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Button onClick={() => setMessageDialog(true)} className="gap-2" data-testid="send-message-btn">
            <Send className="h-4 w-4" />
            Send Message
          </Button>
          <Button variant="outline" onClick={() => setUploadDialog(true)} className="gap-2" data-testid="upload-doc-btn">
            <Upload className="h-4 w-4" />
            Upload Document
          </Button>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-5 max-w-xl">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="cases">Cases</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upcoming Dates */}
              <Card data-testid="upcoming-dates-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-red-500" />
                    Upcoming Dates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {upcoming_dates.length > 0 ? (
                    <div className="space-y-3">
                      {upcoming_dates.map((date, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <div>
                            <p className="font-medium text-slate-900">{date.title}</p>
                            <p className="text-sm text-slate-500">{date.case_title}</p>
                          </div>
                          <Badge variant="outline">{formatDate(date.date)}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">No upcoming dates</p>
                  )}
                </CardContent>
              </Card>

              {/* Pending Invoices */}
              <Card data-testid="pending-invoices-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-orange-500" />
                    Pending Invoices
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pending_invoices.length > 0 ? (
                    <div className="space-y-3">
                      {pending_invoices.map((invoice) => (
                        <div key={invoice.invoice_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <div>
                            <p className="font-medium text-slate-900">{invoice.invoice_number}</p>
                            <p className="text-sm text-slate-500">Due: {formatDate(invoice.due_date)}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold">{formatCurrency(invoice.amount)}</span>
                            <Button size="sm" onClick={() => handlePayInvoice(invoice.invoice_id)}>
                              Pay Now
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                      <p className="text-slate-500">All invoices paid!</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Messages */}
              <Card data-testid="recent-messages-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-blue-500" />
                    Recent Messages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recent_messages.length > 0 ? (
                    <div className="space-y-3">
                      {recent_messages.map((msg) => (
                        <div key={msg.message_id} className="p-3 rounded-lg bg-slate-50">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium text-slate-900">{msg.subject}</p>
                            <span className="text-xs text-slate-400">{formatDate(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-600 line-clamp-2">{msg.content}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">No messages yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Trust Balance */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Trust Account</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <p className="text-4xl font-bold text-green-600">{formatCurrency(client.trust_balance)}</p>
                    <p className="text-sm text-slate-500 mt-2">Available Balance</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Cases Tab */}
          <TabsContent value="cases">
            <Card data-testid="cases-card">
              <CardHeader>
                <CardTitle className="text-lg">Your Cases</CardTitle>
                <CardDescription>Track the status of your legal matters</CardDescription>
              </CardHeader>
              <CardContent>
                {cases.length > 0 ? (
                  <div className="space-y-4">
                    {cases.map((caseItem) => (
                      <div key={caseItem.case_id} className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">{caseItem.title}</h4>
                              <Badge className={getStatusColor(caseItem.status)}>{caseItem.status}</Badge>
                            </div>
                            <p className="text-sm text-slate-500 mt-1">{caseItem.case_number}</p>
                            <Badge variant="outline" className="mt-2">{caseItem.practice_area}</Badge>
                          </div>
                          <div className="text-right text-sm text-slate-500">
                            <p>Opened</p>
                            <p className="font-medium">{formatDate(caseItem.created_at)}</p>
                          </div>
                        </div>
                        {caseItem.description && (
                          <p className="text-sm text-slate-600 mt-3">{caseItem.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Briefcase className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p>No cases found</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card data-testid="documents-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Documents</CardTitle>
                  <CardDescription>View and upload documents</CardDescription>
                </div>
                <Button onClick={() => setUploadDialog(true)} className="gap-2">
                  <Upload className="h-4 w-4" />
                  Upload
                </Button>
              </CardHeader>
              <CardContent>
                {recent_documents.length > 0 ? (
                  <div className="space-y-3">
                    {recent_documents.map((doc) => (
                      <div key={doc.document_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <FileText className="h-8 w-8 text-blue-500" />
                          <div>
                            <p className="font-medium text-slate-900">{doc.name}</p>
                            <p className="text-sm text-slate-500">{formatDate(doc.created_at)}</p>
                          </div>
                        </div>
                        {doc.file_url && (
                          <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm">
                              <Download className="h-4 w-4" />
                            </Button>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p>No documents yet</p>
                    <Button variant="link" onClick={() => setUploadDialog(true)}>
                      Upload your first document
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <Card data-testid="billing-card">
              <CardHeader>
                <CardTitle className="text-lg">Invoices & Payments</CardTitle>
              </CardHeader>
              <CardContent>
                {pending_invoices.length > 0 ? (
                  <div className="space-y-4">
                    {pending_invoices.map((invoice) => (
                      <div key={invoice.invoice_id} className="p-4 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-slate-900">{invoice.invoice_number}</p>
                            <p className="text-sm text-slate-500">{invoice.description || 'Legal Services'}</p>
                            <p className="text-sm text-slate-500 mt-1">Due: {formatDate(invoice.due_date)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-slate-900">{formatCurrency(invoice.amount)}</p>
                            <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-100">
                          <Button onClick={() => handlePayInvoice(invoice.invoice_id)} className="w-full sm:w-auto">
                            Pay Now
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                    <p className="text-slate-900 font-medium">You're all caught up!</p>
                    <p className="text-slate-500">No pending invoices</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card data-testid="messages-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Messages</CardTitle>
                  <CardDescription>Communicate with your legal team</CardDescription>
                </div>
                <Button onClick={() => setMessageDialog(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  New Message
                </Button>
              </CardHeader>
              <CardContent>
                {recent_messages.length > 0 ? (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-4">
                      {recent_messages.map((msg) => (
                        <div 
                          key={msg.message_id} 
                          className={`p-4 rounded-lg ${msg.is_from_client ? 'bg-blue-50 ml-8' : 'bg-slate-50 mr-8'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-slate-900">{msg.subject}</p>
                            <span className="text-xs text-slate-400">{formatDate(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-600">{msg.content}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p>No messages yet</p>
                    <Button variant="link" onClick={() => setMessageDialog(true)}>
                      Send your first message
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Send Message Dialog */}
      <Dialog open={messageDialog} onOpenChange={setMessageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Message</DialogTitle>
            <DialogDescription>Send a message to your legal team</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendMessage} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={messageForm.subject}
                onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                placeholder="What is this regarding?"
                required
              />
            </div>
            {cases.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="case">Related Case (Optional)</Label>
                <Select value={messageForm.case_id} onValueChange={(v) => setMessageForm({ ...messageForm, case_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a case" />
                  </SelectTrigger>
                  <SelectContent>
                    {cases.map((c) => (
                      <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="content">Message</Label>
              <Textarea
                id="content"
                value={messageForm.content}
                onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                placeholder="Type your message..."
                rows={5}
                required
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setMessageDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Upload a document for your legal team</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFileUpload} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="file">Select File</Label>
              <Input
                id="file"
                type="file"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="docName">Document Name</Label>
              <Input
                id="docName"
                value={uploadForm.name}
                onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                placeholder="Leave blank to use filename"
              />
            </div>
            {cases.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="docCase">Related Case (Optional)</Label>
                <Select value={uploadForm.case_id} onValueChange={(v) => setUploadForm({ ...uploadForm, case_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a case" />
                  </SelectTrigger>
                  <SelectContent>
                    {cases.map((c) => (
                      <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="docDesc">Description (Optional)</Label>
              <Textarea
                id="docDesc"
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Brief description of the document"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setUploadDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalDashboard;
